import React from "react";
import { TextNode as RPDFCTextNode } from "@surge-global-engineering/react-pdf-components";
import { Style } from "@surge-global-engineering/rpdf-types";
import {
  PdfSlateBaseParentBlockNode,
  PdfSlateEndnote,
  PdfSlateFootnote,
  PdfSlateLink,
  PdfSlateTextNode,
  PdfTheme,
} from "../types";
import { SMALLCAP_WORD_COUNT } from "../configs";
import TextNode from "./TextNode";
import Dropcap from "./Dropcap";
import { Text as RPDFText } from "@surge-global-engineering/rpdf-renderer";
import { Item } from "@surge-global-engineering/react-pdf-components";

const getFirstParagraphText = (children: (PdfSlateTextNode | PdfSlateEndnote | PdfSlateLink | PdfSlateBaseParentBlockNode)[]) => {
    const text: PdfSlateTextNode[] = [];
    for (let index = 0; index < children.length; index++) {
        const child = children[index] as PdfSlateLink;

        if (!child.children) {
            const _child = children[index] as PdfSlateTextNode;
            text.push(_child);
        }

        if (child.type === "a") {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            text.push(...getFirstParagraphText(child.children));
        }

    }
    return text;
};

const splitTextNodeIntoCharactors = (textNodes: PdfSlateTextNode[]) => {
    const _textNodes: PdfSlateTextNode[] = [];
    for (const textNode of textNodes) {
        const charactors = textNode.text.split("");
        const nodes: PdfSlateTextNode[] = charactors.map(char => ({ ...textNode, text: char }));
        _textNodes.push(...nodes);
    }
    return _textNodes;
};

interface ParagraphProps {
  theme: PdfTheme;
  nodeIndex: number;
  endNotes?: PdfSlateEndnote[];
  endNotesOffset?: number;
  parentType?: string;
  canJustifyText?: boolean;
  indented?: boolean;
  style?: Style;
  /**
   * use for inside the component
   */
  dropcapText?: PdfSlateTextNode | null;
  uppercaseFourWords?: string;
  disableIndent?: boolean;

  /**
   * showFirstSentenceFormatting - A value that can be defined using "Chapter Level" configuration
   * to toggle first sentence formatting on/off.
   *
   * @type {boolean}
   */
  showFirstSentenceFormatting: boolean;
  isHangingIndent?: boolean;
}

const Paragraph: React.FC<ParagraphProps> = ({ theme, nodeIndex, endNotes, endNotesOffset = 0, parentType, canJustifyText, indented, style, dropcapText, uppercaseFourWords, disableIndent, children, showFirstSentenceFormatting, isHangingIndent }) => {
    // eslint-disable-next-line quotes
    const SPECIAL_CHARACTORS = ["'", "“", '"', "‘", " "];
    const { firstParagraph, paragraph, pdfEndnotesChapterSettings } = theme.properties;
    const _children = children as (PdfSlateTextNode | PdfSlateEndnote | PdfSlateLink | PdfSlateBaseParentBlockNode)[];

    if (!children) return <></>;

    // find dropcap charactors
    const firstParagraphText = getFirstParagraphText(_children);
    const firstParagraphTextBeginsWithSpace = (firstParagraphText[0]?.text || "").startsWith(" ", 0);

    // Don't show dropcap
    // - if there are any spaces at the begining of the frist paragraph

    const canShowDropcap = showFirstSentenceFormatting && nodeIndex === 0 && firstParagraph.dropcap && !firstParagraphTextBeginsWithSpace;
    let _dropcapText: PdfSlateTextNode | null = null;
    if (dropcapText && dropcapText?.text !== "") {
        _dropcapText = dropcapText;
    }
    if (canShowDropcap) {
        const textNodes = splitTextNodeIntoCharactors(getFirstParagraphText(_children));
        let letter = "";
        let firstCharactorIndex = textNodes.findIndex(({ text }) => ![""].includes(text));
        if (firstCharactorIndex !== -1) {
            letter = textNodes[firstCharactorIndex].text[0]?.toLocaleUpperCase();
            _dropcapText = { ...textNodes[0], text: letter };
        }
        while (SPECIAL_CHARACTORS.includes(letter) && textNodes[firstCharactorIndex + 1] && textNodes[firstCharactorIndex + 1].text[0] && _dropcapText) {
            letter = textNodes[firstCharactorIndex + 1].text[0]?.toLocaleUpperCase();
            _dropcapText.text = _dropcapText.text + textNodes[firstCharactorIndex + 1].text[0]?.toLocaleUpperCase();
            firstCharactorIndex = firstCharactorIndex + 1;
        }
    }

    const isAllSpecialCharactersBeforeIndex = (index) => {
        for(let i = 0; i < index; i++){
            const text = (_children[i] as PdfSlateTextNode).text?.split("") || [];
            for (const c of text) {
                if(!SPECIAL_CHARACTORS.includes(c)) return false;
            }
        }
        return true;
    };

    // find uppercaseFourWords
    // Don't uppercase first four words
    // - if there are any spaces at the begining of the frist paragraph
    const canUppercaseFourWords = nodeIndex === 0 && firstParagraph.uppercaseFourWords && !firstParagraphTextBeginsWithSpace;
    let _uppercaseFourWords = uppercaseFourWords || "";
    if (canUppercaseFourWords) {
        const text = getFirstParagraphText(_children).map(({ text }) => text).join("")?.split(" ");
        const words = text.slice(0, SMALLCAP_WORD_COUNT);
        _uppercaseFourWords = words.join(" ");
        // remove dropcap charactors
        if (_dropcapText) {
            const chas = _dropcapText?.text?.split("") || [];
            for (const c of chas) {
                if (_uppercaseFourWords.toLocaleUpperCase().startsWith(c)) {
                    _uppercaseFourWords = _uppercaseFourWords.substring(1);
                }
            }
        }
    }

    const isDropOrphanWidowProtection = theme?.properties.layoutPriority === "BALANCED_PAGE_SPREAD";
    const isApplyAdvancePageBalancing = theme?.properties.layoutPriority === "BEST_OF_BOTH";

    const shouldBreakIfLastOnPage = ["h2", "h3", "h4", "h5", "h6"].includes(parentType as string) && theme?.properties.dynamicPageBreaks?.breakSubheadings;
    const showDropcap = canShowDropcap && !!_dropcapText?.text;
    const textNodeStyle = { ...style, minHeight: showDropcap ? theme.properties.baseFontSize * 2 : undefined, dynamicLetterSpacing: isApplyAdvancePageBalancing };

    const orphans = isDropOrphanWidowProtection ? 0 : 2;
    const widows = isDropOrphanWidowProtection ? 0 : 2;

    return (
        <>
            {showDropcap && <Dropcap theme={theme}>{_dropcapText}</Dropcap>}
            <RPDFCTextNode style={textNodeStyle} breakIfLastOnPage={shouldBreakIfLastOnPage} orphans={orphans} widows={widows}>
                {_children.map((child, index) => {
                    const { type } = child as PdfSlateEndnote | PdfSlateLink | PdfSlateBaseParentBlockNode;

                    // render links
                    if (type === "a") {
                        const { children: linkChildren } = child as PdfSlateLink;
                        return (
                            <Paragraph
                                key={`para-${index}`}
                                theme={theme}
                                endNotes={endNotes}
                                nodeIndex={nodeIndex + index}
                                canJustifyText={canJustifyText}
                                isHangingIndent={isHangingIndent}
                                dropcapText={_dropcapText}
                                uppercaseFourWords={_uppercaseFourWords}
                                parentType={parentType}
                                showFirstSentenceFormatting={showFirstSentenceFormatting}
                            >
                                {linkChildren}
                            </Paragraph>
                        );
                    }

                    // render endnotes
                  if (type === "endnote") {
                    const { id: endNoteId, note: endNotesNote } =
                      child as PdfSlateEndnote;

                    /**
                     * Check comments in getAllEndNotes()
                     * If the endnote id manually assigned inside the getAllEndNotes(), here endNoteId is undefined
                     * Therefore, has to compare note strings and find the relevant index
                     */
                    let endNoteIndex: number | undefined;
                    if (endNoteId) {
                      endNoteIndex = endNotes?.findIndex(({ id }) => id === endNoteId);
                    } else {
                      endNoteIndex = endNotes?.findIndex(
                        ({ note }) => note === endNotesNote
                      );
                    }

                    if (endNoteIndex === undefined) return <></>;

            if (theme.properties.notesMode === "FOOTNOTE" ) {

              const renderEndNotes = () => {
                if (typeof endNotesNote === "string") {
                  return (
                    <RPDFText
                      key={`text-${endNoteIndex}`}
                      style={{ fontFamily: theme.properties.printBaseFont, fontSize: theme.properties.baseFontSize * theme.properties.footnoteFontSize }}
                    >
                      {endNotesNote}
                    </RPDFText>
                  );
                } else {
                  // Remove empty text nodes from the link
                  const filteredEndnotes = (endNotesNote[0] as PdfSlateFootnote).children.filter(child=> child.text !="");

                  return (filteredEndnotes).map((footnote, index) => {
                    return footnote.type === "a" && footnote.children ? (     

                      <TextNode
                        key={`footnote-link-${endNoteIndex}-${index}`}
                        theme={theme}
                        textNode={footnote.children[0]}
                        isIndented={false}
                        parentType={"footnote"}
                        canJustifyText={canJustifyText}
                        canHaveSoftBreaks={false}
                      />
                    ) : (
                      
                      <TextNode
                        key={`footnote-${endNoteIndex}-${index}`}
                        theme={theme}
                        textNode={footnote}
                        isIndented={false}
                        parentType={"footnote"}
                        canJustifyText={canJustifyText}
                        canHaveSoftBreaks={false}
                      />
                    );
                  });
                }
              };

              return (
                <RPDFText
                  key={`footnote-${endNoteIndex + index}`}
                  footnote={() => (
                    <Item prefix={`${(endNoteIndex! + 1).toString()}.`} style={{ fontFamily: theme.properties.printBaseFont, fontSize: theme.properties.baseFontSize * theme.properties.footnoteFontSize}}>
                      <>{renderEndNotes()}</>
                    </Item>
                  )}
                  style={{ fontFamily: theme.properties.printBaseFont, fontSize: theme.properties.baseFontSize, fontVariant: "superscript" }}
                >
                  {(endNoteIndex! + 1).toString()}{" "}
                </RPDFText>
              );
            }

                    return (
                      <TextNode
                        key={`text-${index}`}
                        theme={theme}
                        parentType="endnote"
                        canJustifyText={canJustifyText}
                        textNode={pdfEndnotesChapterSettings.categorizeByTitle ?
                          { text: `${endNoteIndex + 1}` } : { text: `${endNoteIndex + 1 + endNotesOffset}` }}
                      />
                    );
                  }

                    let _child = child as PdfSlateTextNode;
                    let _text = _child.text;
                    if (nodeIndex === 0 && index === 0) {
                        if (_text === "" && !(canShowDropcap && !!_dropcapText?.text)) return (
                            <TextNode
                                key={`text-${index}`}
                                theme={theme}
                                textNode={_child}
                                isIndented={false}
                                parentType={parentType}
                                canJustifyText={canJustifyText}
                                isHangingIndent={isHangingIndent}
                            />);
                    }

                    // remove dropcap charactors from textnode
                    if(nodeIndex == 0 && canShowDropcap && _dropcapText && isAllSpecialCharactersBeforeIndex(index+1)){
                        return <></>;
                    }

                    if (nodeIndex == 0
                        && isAllSpecialCharactersBeforeIndex(index)
                        && canShowDropcap
                        && _dropcapText) {
                        _text = "";
                        for(let i = 0; i <= index; i++) {
                            _text = _text + (_children[i] as PdfSlateTextNode).text;
                        }
                        const chas = _dropcapText?.text?.split("") || [];
                        for (const c of chas) {
                            if (_text.toLocaleUpperCase().startsWith(c)) {
                                _text = _text.substring(1);
                            }
                        }
                        if (_text === "") return <></>;
                        _child = { ..._child, text: _text };
                    }

                    // render uppercaseFourWords
                    if (_uppercaseFourWords !== "" && showFirstSentenceFormatting) {
                        const chars = _uppercaseFourWords.split("") || [];
                        _text = _child.text;
                        const smallCapsChars: string[] = [];

                        for (const c of chars) {
                            if (_text.startsWith(c)) {
                                smallCapsChars.push(c);
                                _text = _text.substring(1);
                            }
                        }

                        const tempUpercaseFourWords = smallCapsChars.join("");
                        _uppercaseFourWords = _uppercaseFourWords.replace(tempUpercaseFourWords, "");

                        if (smallCapsChars.length === 0) {
                            _uppercaseFourWords = "";
                        }

                        if (_text === "") return <TextNode
                            key={`text-${index}`}
                            theme={theme}
                            canJustifyText={canJustifyText}
                            textNode={{ ..._child, text: tempUpercaseFourWords, smallcaps: true }}
                            parentType={parentType}
                        />;
                        _child = { ..._child, text: _text };

                        return (
                            <>
                                <TextNode
                                    key={`text-smc-${index}`}
                                    theme={theme}
                                    textNode={{ ..._child, text: tempUpercaseFourWords, smallcaps: true }}
                                    canJustifyText={canJustifyText}
                                    parentType={parentType}
                                />
                                <TextNode
                                    key={`text-${index}`}
                                    theme={theme}
                                    textNode={_child}
                                    canJustifyText={canJustifyText}
                                    parentType={parentType}
                                />
                            </>
                        );
                    }

                    let isIndented = false;
                    if (indented === undefined) {
                        if (paragraph.indent && (nodeIndex !== 0 || firstParagraphTextBeginsWithSpace && nodeIndex === 0)) {
                            isIndented = true;
                        }
                        if (dropcapText?.text || canUppercaseFourWords || disableIndent) {
                            isIndented = false;
                        }
                    }
                    if (indented !== undefined) {
                        if (firstParagraphTextBeginsWithSpace && nodeIndex === 0) {
                            isIndented = !!paragraph.indent;
                        } else {
                            isIndented = indented;
                        }
                    }

                    // render normal text
                    return (
                        <TextNode
                            key={`text-${index}`}
                            theme={theme}
                            textNode={_child}
                            isIndented={isIndented}
                            isHangingIndent={isHangingIndent}
                            parentType={parentType}
                            canJustifyText={canJustifyText}
                            canHaveSoftBreaks={true} // allow soft breaks on normal text
                        />
                    );
                })}
            </RPDFCTextNode>
        </>
    );
};

export default Paragraph;
